import { Button } from "@faire/web--source/slate/Button";
import { Column } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import brandPortalMarketplace from "@faire/web-api--source/routes/brand-portal/marketplace";
import brandPortalShopSettings from "@faire/web-api--source/routes/brand-portal/my-shop/settings";
import React, { type JSX } from "react";

import { Bold } from "../common/Bold";
import { LiItem } from "../common/LiItem";

export const OrderMinimumsIA = () => {
  return (
    <Column>
      <Typography>
        <StrictLocalMsg
          defaultMessage="You can set your shop order minimums in <LinkFormatting>Shop settings</LinkFormatting> to fit your brand’s needs. To place an order, retailers must meet the minimum order value that you set. You may set different minimums for a first order and for a reorder."
          values={{
            LinkFormatting: ShopSettingsLinkFormatting,
          }}
          description={{
            text: "Introduction to setting order minimums",
          }}
        />
      </Typography>
      <Spacer height="1x" />
      <Typography>
        <StrictLocalMsg
          defaultMessage="You also have the option to set minimums in each country where your shop is available. These minimums can be set in each country’s local currency."
          description={{
            text: "Information about setting international order minimums",
          }}
        />
      </Typography>
      <Spacer height="1x" />
      <Typography>
        <StrictLocalMsg
          defaultMessage="Here’s how to make changes:"
          description={{
            text: "Header for steps to change order minimums",
          }}
        />
      </Typography>
      <Spacer height="1x" />
      <ol>
        <LiItem step={1}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Navigate to <LinkFormatting>Marketplace</LinkFormatting>."
              values={{
                LinkFormatting: MarketplaceLinkFormatting,
              }}
              description={{
                text: "First step to change order minimums",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={2}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Select <LinkFormatting>Shop settings</LinkFormatting> in the dropdown menu."
              values={{
                LinkFormatting: ShopSettingsLinkFormatting,
              }}
              description={{
                text: "Second step to change order minimums",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={3}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Scroll to <BoldFormatting>Order minimums</BoldFormatting> to change your first order and reorder minimum."
              values={{
                BoldFormatting,
              }}
              description={{
                text: "Third step to change order minimums",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={4}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="To set different order minimums internationally, scroll to <BoldFormatting>International orders</BoldFormatting> and change minimums for a region or country."
              values={{
                BoldFormatting,
              }}
              description={{
                text: "Fourth step to change order minimums",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={5}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="<BoldFormatting>Save</BoldFormatting> any changes."
              values={{
                BoldFormatting,
              }}
              description={{
                text: "Fifth step to change order minimums",
              }}
            />
          </Typography>
        </LiItem>
      </ol>
    </Column>
  );
};

const ShopSettingsLinkFormatting = (chunks: JSX.Element) => (
  <Button variant="tertiary" linkTo={brandPortalShopSettings()} target="_blank">
    <Bold>{chunks}</Bold>
  </Button>
);

const MarketplaceLinkFormatting = (chunks: JSX.Element) => (
  <Button variant="tertiary" linkTo={brandPortalMarketplace()} target="_blank">
    <Bold>{chunks}</Bold>
  </Button>
);

const BoldFormatting = (chunks: JSX.Element) => <Bold>{chunks}</Bold>;
